import type { User as FirebaseUser } from 'firebase/auth';

export type UserRole =
  | 'TITOLARE'
  | 'DIETOLOGA'
  | 'SEGRETERIA'
  | 'ADMIN'
  | 'PAZIENTE';

export type MfaTier = 0 | 1 | 2;

export const STAFF_ROLES: ReadonlyArray<UserRole> = [
  'TITOLARE',
  'DIETOLOGA',
  'SEGRETERIA',
  'ADMIN',
];

export function isStaffRole(role: UserRole | null): boolean {
  return role !== null && STAFF_ROLES.includes(role);
}

export interface UserDoc {
  email: string;
  displayName: string;
  role: UserRole;
  mfaEnrolled: boolean;
  mustChangePasswordAtNextLogin?: boolean;
  flagged?: boolean;
  dietologaId?: string;
  /**
   * PR-BO: mirror del claim `bo`, derivato dall'email. Vincola le query su
   * /users. Opzionale perche' i documenti pre-backfill ne sono privi.
   */
  soloBackoffice?: boolean;
  createdAt: Date | string;
  updatedAt?: Date | string;
}

export interface AuthTokenClaims {
  role: UserRole;
  auth_time: number;
  /** PR-BO: account di backoffice. Assente sugli account normali. */
  bo?: boolean;
  /**
   * PR-BO-DELEGA: uid della dietologa di cui un account di backoffice
   * gestisce i pazienti. Vale in lettura e in scrittura, e solo con `bo`
   * presente.
   */
  bodeleg?: string;
}

export interface AuthState {
  user: FirebaseUser | null;
  role: UserRole | null;
  mfaTier: MfaTier;
  mustChangePassword: boolean;
  mfaEnrolled: boolean;
  /**
   * PR-BO: true se il token porta il claim `bo`. Decide se le query
   * client devono aggiungere `where('soloBackoffice','==',false)`.
   * E' UX/query-shaping: l'enforcement reale sta nelle Firestore Rules.
   */
  isBackoffice: boolean;
  /**
   * PR-BO-DELEGA — uid da usare come scope nelle query che filtrano per
   * dietologa. `null` per ogni ruolo diverso da DIETOLOGA (vista
   * studio-wide). Per la DIETOLOGA e' il proprio uid, oppure l'uid
   * delegante se l'account e' di backoffice con delega.
   *
   * Esposto gia' calcolato invece di lasciare che ogni call site rifaccia
   * `role === 'DIETOLOGA' ? uid : undefined`: la regola di scope e' un
   * vincolo di sicurezza (senza il where la query viene rifiutata) e non
   * va replicata a mano in cinque punti diversi.
   */
  dietologaScopeUid: string | null;
  /**
   * PR-BO-DELEGA — uid con cui valutare l'APPARTENENZA di un dato lato UI:
   * assegnazione del paziente, paternita' di una nota, di un allegato o di
   * un piano. Coincide con `user.uid` per tutti tranne un account di
   * backoffice delegato, per cui e' l'uid della delegante.
   *
   * Diverso da `dietologaScopeUid` per un motivo pratico: quello e' `null`
   * sui ruoli studio-wide, perche' serve a decidere se una query porta un
   * `where`. Questo invece e' sempre valorizzato (se c'e' un utente), e
   * serve dove il confronto va fatto comunque, ad esempio `nota.createdBy
   * === uidOperativo`.
   *
   * DEVE combaciare con l'identita' che il server usa negli stessi
   * confronti (`scopeDietologa` nelle callable): se divergono, l'interfaccia
   * mostra azioni che il server rifiuta, o le nasconde pur essendo
   * permesse.
   */
  uidOperativo: string | null;
  loading: boolean;
  error: Error | null;
}
